import React from 'react';
import {
  Plus, Minus, Trash2, CreditCard, Banknote, Receipt, ShoppingCart,
  Percent, X, Check, XCircle, AlertTriangle, User, Star, DollarSign, Clock
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export const POSCartPanel = ({
  cart, updateQuantity, removeFromCart, totals,
  customerName, setCustomerName, selectedCustomer, setSelectedCustomer,
  loadCustomers, setShowCustomerDialog,
  appliedPromo, setAppliedPromo, setShowPromoDialog,
  loyaltyPointsToRedeem, setLoyaltyPointsToRedeem, calculateLoyaltyPointsEarned,
  discount, setShowDiscountDialog,
  setPaymentMethod, setCashAmount, setShowPaymentDialog,
  setSplitPayment, currentShift, setShowVoidDialog,
  requiresAgeVerification, ageVerified, verificationLog, setShowAgeVerifyDialog,
  // Hold & Recall
  heldOrders, holdCurrentOrder, setShowHeldOrdersDialog,
  // Refund
  setShowRefundDialog,
}) => {

  const handlePay = (method) => {
    if (requiresAgeVerification()) {
      toast.error('Age verification required');
      setShowAgeVerifyDialog(true);
      return;
    }
    if (method === 'split') {
      setSplitPayment({ enabled: true, cashAmount: '', cardAmount: '' });
    }
    setPaymentMethod(method);
    if (method === 'cash') setCashAmount(totals.total);
    setShowPaymentDialog(true);
  };

  const disabled = cart.length === 0 || !currentShift?.id;

  return (
    <Card className="w-[380px] flex flex-col bg-white dark:bg-slate-800 shadow-xl" data-testid="pos-cart-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="flex items-center gap-2 font-semibold text-sm">
          <ShoppingCart className="h-4 w-4" /> Cart
          {cart.length > 0 && (
            <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
              {cart.reduce((a, b) => a + b.quantity, 0)}
            </Badge>
          )}
        </span>
        {cart.length > 0 && (
          <button onClick={() => setShowVoidDialog(true)}
            className="text-red-500 hover:text-red-600 text-xs flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5" /> Void
          </button>
        )}
      </div>

      <div className="flex-1 flex flex-col overflow-hidden px-3 py-2">
        {/* Quick Actions: Hold | Recall | Refund */}
        <div className="flex gap-1 mb-2">
          <button onClick={holdCurrentOrder} disabled={cart.length === 0}
            className="flex-1 flex items-center justify-center gap-1 h-7 text-[11px] font-medium rounded bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 disabled:opacity-40 transition-colors"
            data-testid="hold-order-btn">
            <Clock className="h-3 w-3" /> Hold
          </button>
          <button onClick={() => setShowHeldOrdersDialog(true)}
            className="flex-1 flex items-center justify-center gap-1 h-7 text-[11px] font-medium rounded bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 transition-colors relative"
            data-testid="recall-order-btn">
            <ShoppingCart className="h-3 w-3" /> Recall
            {heldOrders.length > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center">
                {heldOrders.length}
              </span>
            )}
          </button>
          <button onClick={() => setShowRefundDialog(true)}
            className="flex-1 flex items-center justify-center gap-1 h-7 text-[11px] font-medium rounded bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-100 transition-colors"
            data-testid="refund-btn">
            <DollarSign className="h-3 w-3" /> Return
          </button>
        </div>

        {/* Customer — compact row */}
        <div className="flex gap-1.5 mb-2">
          {selectedCustomer ? (
            <div className="flex-1 flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-950 rounded border border-blue-200 dark:border-blue-800 text-xs">
              <User className="h-3 w-3 text-blue-600" />
              <span className="font-medium truncate">{selectedCustomer.name}</span>
              <span className="text-blue-500">{selectedCustomer.loyalty_points || 0}pts</span>
              <button onClick={() => setSelectedCustomer(null)} className="ml-auto"><X className="h-3 w-3" /></button>
            </div>
          ) : (
            <>
              <Input placeholder="Customer" value={customerName}
                onChange={(e) => setCustomerName(e.target.value)} className="h-8 text-xs" />
              <Button variant="outline" size="icon" className="h-8 w-8 shrink-0"
                onClick={() => { loadCustomers(); setShowCustomerDialog(true); }}>
                <User className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>

        {/* Quick action row: Promo | Discount | Loyalty redemption — collapsed into icons */}
        <div className="flex gap-1.5 mb-2">
          {appliedPromo ? (
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-950 rounded border border-emerald-200 dark:border-emerald-800 text-xs flex-1">
              <Receipt className="h-3 w-3 text-emerald-600" />
              <span className="text-emerald-700 dark:text-emerald-300 font-medium truncate">
                {appliedPromo.code}: -{appliedPromo.discount_percent ? `${appliedPromo.discount_percent}%` : `$${appliedPromo.discount_amount}`}
              </span>
              <button onClick={() => setAppliedPromo(null)} className="ml-auto"><X className="h-3 w-3" /></button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => setShowPromoDialog(true)}>
              <Receipt className="h-3 w-3" /> Promo
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={() => setShowDiscountDialog(true)}>
            <Percent className="h-3 w-3" />
            {discount.value > 0 ? `${discount.type === 'percent' ? `${discount.value}%` : `$${discount.value}`}` : 'Discount'}
          </Button>
        </div>

        {/* Loyalty points redemption — only when customer has points */}
        {selectedCustomer && selectedCustomer.loyalty_points > 0 && (
          <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-purple-50 dark:bg-purple-950 rounded border border-purple-200 dark:border-purple-800 text-xs">
            <Star className="h-3 w-3 text-purple-600" />
            <span className="text-purple-700 dark:text-purple-300">Redeem pts</span>
            <Input type="number" value={loyaltyPointsToRedeem}
              onChange={(e) => setLoyaltyPointsToRedeem(Math.min(parseInt(e.target.value) || 0, selectedCustomer.loyalty_points))}
              className="h-6 w-16 text-xs text-center" max={selectedCustomer.loyalty_points} />
            <span className="text-purple-500">/{selectedCustomer.loyalty_points}</span>
          </div>
        )}

        {/* Age warnings — compact inline */}
        {requiresAgeVerification() && (
          <button onClick={() => setShowAgeVerifyDialog(true)}
            className="flex items-center justify-center gap-1.5 mb-2 px-2 py-1.5 bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-800 rounded text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors">
            <AlertTriangle className="h-3.5 w-3.5" /> 21+ Verify Required — Tap to verify
          </button>
        )}
        {ageVerified && cart.some(item => item.age_restricted) && (
          <div className="flex items-center justify-center gap-1 mb-2 text-xs text-emerald-600 dark:text-emerald-400">
            <Check className="h-3 w-3" /> Age verified ({verificationLog?.age}yrs)
          </div>
        )}

        {/* Cart Items — takes all remaining space */}
        <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0">
          {cart.map(item => (
            <div key={item.product_id} className="flex items-center gap-1.5 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate leading-tight">{item.name}</p>
                <p className="text-xs text-muted-foreground">
                  ${item.unit_price.toFixed(2)} x {item.quantity} = <span className="font-medium text-foreground">${(item.unit_price * item.quantity).toFixed(2)}</span>
                </p>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Button variant="outline" size="icon" className="h-7 w-7"
                  onClick={() => updateQuantity(item.product_id, -1)}>
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-7 text-center text-sm font-bold">{item.quantity}</span>
                <Button variant="outline" size="icon" className="h-7 w-7"
                  onClick={() => updateQuantity(item.product_id, 1)}>
                  <Plus className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600"
                  onClick={() => removeFromCart(item.product_id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}

          {cart.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p className="text-sm">Cart is empty</p>
            </div>
          )}
        </div>

        {/* Totals — compact */}
        <div className="mt-2 pt-2 border-t space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Subtotal</span>
            <span>${totals.subtotal}</span>
          </div>
          {parseFloat(totals.discountAmount) > 0 && (
            <div className="flex justify-between text-emerald-600 text-xs">
              <span>Discount</span>
              <span>-${totals.discountAmount}</span>
            </div>
          )}
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">
              {totals.taxState ? `${totals.taxState} Tax (${totals.taxRate}%)` : `Tax (${totals.taxRate}%)`}
            </span>
            <span>${totals.taxAmount}</span>
          </div>
          <div className="flex justify-between text-xl font-bold pt-1 border-t">
            <span>Total</span>
            <span className="text-primary">${totals.total}</span>
          </div>
        </div>

        {/* Payment — 3 compact buttons in one row */}
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          <Button className="h-10 text-sm bg-emerald-600 hover:bg-emerald-700 gap-1"
            onClick={() => handlePay('cash')} disabled={disabled}>
            <Banknote className="h-4 w-4" /> Cash
          </Button>
          <Button className="h-10 text-sm bg-blue-600 hover:bg-blue-700 gap-1"
            onClick={() => handlePay('card')} disabled={disabled}>
            <CreditCard className="h-4 w-4" /> Card
          </Button>
          <Button variant="outline" className="h-10 text-sm gap-1"
            onClick={() => handlePay('split')} disabled={disabled}>
            <DollarSign className="h-4 w-4" /> Split
          </Button>
        </div>

        {/* Loyalty earned — tiny inline */}
        {cart.length > 0 && (
          <p className="text-center text-[10px] text-purple-500 mt-1.5 flex items-center justify-center gap-1">
            <Star className="h-2.5 w-2.5" /> +{calculateLoyaltyPointsEarned()} loyalty pts
          </p>
        )}

        {!currentShift?.id && cart.length > 0 && (
          <p className="text-center text-amber-600 text-xs mt-1 flex items-center justify-center gap-1">
            <AlertTriangle className="h-3 w-3" /> Open shift to pay
          </p>
        )}
      </div>
    </Card>
  );
};
